/**
 * CatalogTopFilters
 *
 * Horizontal filter strip above the course catalog. Replaces the
 * standing-left filter panel for learners.
 *
 * What sits here: search, tags (admin), published date range,
 * workspace (admin), "Only Published" + "Show Assigned Only" toggles.
 * Same handler shape as the old `FilterPanel` so the surrounding
 * Course.tsx wiring doesn't change.
 */

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '../ui';
import { SearchIcon } from '../icons';
import { Tag, TagType, Workspace } from '../../apis/api-client';
import { TagAutocomplete } from '../common';
import WorkspaceAutocomplete from '../common/WorkspaceAutocomplete';
import { tagScopeForUser } from '../../utils/tagFilters';
import { isAdmin as isAdminRole } from '../../utils/roleHelpers';
import { RootState } from '../../types';
import CatalogFacetDropdown from './CatalogFacetDropdown';

/** Catalog "Type" facet — replaces the old All Courses / Learning
 *  Paths tab strip and lives in this top filter bar. */
export type CatalogTypeValue = 'Courses' | 'Learning Paths';

/** Catalog sort options. Applied client-side over the loaded course
 *  list — no server-side sort param is available today. */
export type CatalogSortBy = 'title' | 'publishedDate' | 'lastUpdated';

export const CATALOG_SORT_OPTIONS: { value: CatalogSortBy; label: string }[] = [
  { value: 'title', label: 'Title' },
  { value: 'publishedDate', label: 'Publish date' },
  { value: 'lastUpdated', label: 'Last updated' },
];

export interface CatalogTopFiltersProps {
  filters: any;
  onFilterChange: (next: any) => void;
  onClearFilters: () => void;
  activeFilterCount: number;
  isAuthenticated?: boolean;
  /** Type facet — Courses / Learning Paths. Empty = both shown. */
  typeFilter: CatalogTypeValue[];
  onTypeFilterChange: (next: CatalogTypeValue[]) => void;
  /** Counts for the Type facet options. */
  typeCounts?: { courses?: number; paths?: number };
}

export default function CatalogTopFilters({
  filters,
  onFilterChange,
  onClearFilters,
  activeFilterCount,
  isAuthenticated,
  typeFilter,
  onTypeFilterChange,
  typeCounts,
}: CatalogTopFiltersProps) {
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const switcherWorkspace = useSelector(
    (state: RootState) => state.workspaces.selectedWorkspace,
  );

  const isAdmin = isAdminRole(currentUser?.role);
  // Workspace filter is admin-only and hidden when the global switcher
  // already pins a workspace (the filter would just confuse the scope).
  const showWorkspaceFilter = isAdmin && !switcherWorkspace;
  // Tags are admin metadata — learners don't filter by them.
  const canSeeTags = isAdmin;

  // Debounced search — wait 300ms after the last keystroke before
  // firing a fetch so admins can type a multi-word query without a
  // request per character.
  const [searchText, setSearchText] = useState(filters.name || '');
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchText !== (filters.name || '')) {
        onFilterChange({ ...filters, name: searchText || undefined });
      }
    }, 300);
    return () => clearTimeout(t);
    // `filters` must be a dep: the parent replaces the whole filter object,
    // so a timeout firing with a stale `filters` would clobber a facet the
    // user changed during the 300ms window. The stable `onFilterChange` is
    // intentionally omitted (adding the unmemoized prop resets debounce each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, filters]);
  // External clears (e.g. Clear All) — sync the input back to upstream.
  useEffect(() => {
    if (!filters.name && searchText !== '') setSearchText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.name]);

  // Selected tag/workspace objects, kept in local state for chip render
  // (upstream stores IDs only).
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);

  const handleTagChange = (tags: Tag[]) => {
    setSelectedTags(tags);
    const ids = tags.map((t) => t.id).filter((id): id is number => !!id);
    onFilterChange({ ...filters, tagIds: ids.length > 0 ? ids : undefined });
  };

  const handleWorkspaceChange = (ws: Workspace | null) => {
    setSelectedWorkspace(ws);
    onFilterChange({ ...filters, workspaceId: ws?.id ?? undefined });
  };

  const handleSortChange = (value: string) => {
    onFilterChange({ ...filters, sortBy: value as CatalogSortBy });
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        // Single hairline rule above the header — separates the
        // catalog section from the banner / activity panel that sit
        // above it.
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header — sits above the controls so the bar reads as the
          gateway into the whole catalog rather than a stray filter
          strip. h5 (not h6) so the theme's heavier weight + tighter
          tracking actually carry visual weight on the page. */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="h5">Entire Course Catalog</Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          pb: 2,
        }}
      >
        {/* Search — primary affordance, wider than the others. */}
        <TextField
          size="small"
          placeholder="Search courses…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: '1 1 240px', minWidth: 200 }}
        />

        {/* Tag filter — admin metadata. */}
        {canSeeTags && (
          <Box sx={{ flex: '1 1 220px', minWidth: 200 }}>
            <TagAutocomplete
              multiple
              value={selectedTags}
              onChange={(v) => handleTagChange((v as Tag[]) ?? [])}
              tagType={TagType.CourseTag}
              {...tagScopeForUser(currentUser?.role, currentUser?.workspaceId)}
              placeholder="Filter by tag"
              size="small"
            />
          </Box>
        )}

        {/* Workspace filter — admin only. */}
        {showWorkspaceFilter && (
          <Box sx={{ flex: '0 1 220px', minWidth: 200 }}>
            <WorkspaceAutocomplete
              value={selectedWorkspace}
              onChange={handleWorkspaceChange}
              placeholder="All workspaces"
              size="small"
            />
          </Box>
        )}

        {/* Push facets + sort to the right edge. */}
        <Box sx={{ flex: 1 }} />

        {isAdmin && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filters.isDraft === false}
                onChange={(e) =>
                  onFilterChange({ ...filters, isDraft: e.target.checked ? false : undefined })
                }
              />
            }
            label="Only Published"
            sx={{ ml: 0 }}
          />
        )}

        {/* Type facet — replaces the old All Courses / Learning Paths
            tab strip. */}
        <CatalogFacetDropdown
          label="Type"
          options={[
            { value: 'Courses', count: typeCounts?.courses },
            { value: 'Learning Paths', count: typeCounts?.paths },
          ]}
          selected={typeFilter}
          onChange={(next) => onTypeFilterChange(next as CatalogTypeValue[])}
        />

        {/* Sort by — last in the row. Presentation choice, not a
            narrowing filter, so it sits separate from the facets. */}
        <TextField
          select
          size="small"
          label="Sort by"
          value={filters.sortBy || 'title'}
          onChange={(e) => handleSortChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        >
          {CATALOG_SORT_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Active filter pills + Clear All — only when something's set. */}
      {activeFilterCount > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap',
            px: 2,
            pb: 2,
          }}
        >
          <Chip
            label={`${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} active`}
            size="small"
            sx={{
              height: 22,
              fontWeight: 600,
              bgcolor: 'rgba(var(--brand-rgb), 0.18)',
              color: 'primary.main',
            }}
          />
          <Button
            size="small"
            variant="text"
            onClick={onClearFilters}
            sx={{ textTransform: 'none', minWidth: 0, fontSize: '0.75rem' }}
          >
            Clear all
          </Button>
        </Box>
      )}
    </Box>
  );
}
